import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, FileTextIcon, Loader2Icon, MessageSquareIcon, RotateCcwIcon } from 'lucide-react';
import { boothChapters, boothDisclaimer } from '../../data/booth';
interface BoothSummaryProps {
  visited: string[];
  onRestart: () => void;
  onBack: () => void;
}
type FollowUp = 'idle' | 'sending' | 'done';
const ease = [0.22, 1, 0.36, 1] as const;
export function BoothSummary({
  visited,
  onRestart,
  onBack
}: BoothSummaryProps) {
  const [status, setStatus] = useState<FollowUp>('idle');
  const timer = useRef<number | null>(null);
  const explored = boothChapters.filter(c => visited.includes(c.id));
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  const request = () => {
    setStatus('sending');
    timer.current = window.setTimeout(() => setStatus('done'), 800);
  };
  return <div className="grid h-full grid-cols-[1.1fr_1fr] gap-8 overflow-y-auto px-10 py-8 no-scrollbar" data-id="element-103" data-mp="zy6myylno5mk">
      <div data-id="element-104" data-mp="12b3fea0r723a">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-iris" data-id="element-105" data-mp="12attaxitxf2v">Your visit</p>
        <h2 className="mt-3 font-serif text-[40px] leading-[1.04] text-navy-900" data-id="element-106" data-mp="sj7hayyo3bz2">
          Different starting points. <span className="text-pulse-iris" data-id="element-107" data-mp="nke6jearlz2l">One conversation.</span>
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-500" data-id="element-108" data-mp="ni9yck5u2dwi">
          You explored {explored.length} of {boothChapters.length} chapters today.
        </p>
        <ul className="mt-6 space-y-3" data-id="element-109" data-mp="ehv4uxiptshx">
          {explored.map((c, i) => <motion.li key={c.id} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.45,
          delay: 0.1 + i * 0.08,
          ease
        }} className="flex items-center gap-4 rounded-2xl border border-navy-900/[0.08] bg-white p-3" data-id="element-110" data-mp="9ledlug7yy0m" data-mpi={i}>
              <img src={c.image} alt="" className="h-14 w-14 rounded-xl object-cover" data-id="element-111" data-mp="1yx6s27cypra7" />
              <div data-id="element-112" data-mp="3te6tvuy6h5t7">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{
              color: c.accent
            }} data-id="element-113" data-mp="3tdna08oyw7cj">
                  {c.number} · {c.title}
                </p>
                <p className="mt-0.5 text-[14px] text-navy-900" data-id="element-114" data-mp="3jj55x2fn29s7">{c.takeaway}</p>
              </div>
            </motion.li>)}
        </ul>
      </div>

      <div className="flex flex-col gap-4" data-id="element-115" data-mp="g0216yq2x7qr">
        <section className="rounded-3xl bg-navy-900 p-6 text-white" data-id="element-116" data-mp="g1d5cznfshie">
          <MessageSquareIcon className="h-5 w-5 text-teal-300" data-id="element-117" data-mp="kyn63bga0flj" />
          <h3 className="mt-3 text-[20px] font-semibold leading-snug" data-id="element-118" data-mp="62nz644wklhz">Continue the conversation</h3>
          <p className="mt-2 text-[13px] leading-relaxed text-white/70" data-id="element-119" data-mp="b18oh8im9krn">
            Request a follow-up from a Ferring representative or a medical information response.
          </p>
          <AnimatePresence mode="wait" initial={false} data-id="element-120" data-mp="2quzha4lgs0n3">
            {status === 'done' ? <motion.p key="done" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} role="status" className="mt-5 flex items-start gap-2 rounded-2xl bg-white/10 p-4 text-[13px] leading-relaxed" data-id="element-121" data-mp="2vpd8rvx09nmh">
                <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" data-id="element-122" data-mp="1msgd2zbe4y3g" />
                Request noted for this demo. No personal data has been collected — a live version would use an approved
                consent and data process.
              </motion.p> : <motion.button key="cta" type="button" onClick={request} disabled={status === 'sending'} className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white text-[14px] font-semibold text-navy-900 transition hover:bg-mist-100 disabled:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300" data-id="element-123" data-mp="q2flytnju5iz">
                {status === 'sending' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-124" data-mp="k4uorray2e94" />}
                {status === 'sending' ? 'Sending…' : 'Request a follow-up'}
              </motion.button>}
          </AnimatePresence>
        </section>

        <section className="flex items-center gap-4 rounded-3xl border border-navy-900/[0.08] bg-white p-5" data-id="element-125" data-mp="tzmmcv4j113w">
          <FileTextIcon className="h-5 w-5 shrink-0 text-pulse-iris" data-id="element-126" data-mp="13r97bj6o2eju" />
          <p className="text-[13px] leading-relaxed text-slate-600" data-id="element-127" data-mp="13qzgjtdss3d3">
            Prescribing information and references are available from the Ferring team at the booth.
          </p>
        </section>

        <div className="mt-auto flex gap-2" data-id="element-128" data-mp="1csqgsipkjhzk">
          <button type="button" onClick={onBack} className="flex-1 rounded-full border border-navy-900/10 px-4 py-3 text-[13px] font-medium text-navy-800 transition hover:bg-mist-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-129" data-mp="1hrjx0iw8pn3f">
            Explore another chapter
          </button>
          <button type="button" onClick={onRestart} className="flex items-center gap-2 rounded-full px-4 py-3 text-[13px] text-slate-500 transition hover:text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-130" data-mp="3c0yura3i0vzf">
            <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-131" data-mp="2xbsy9tw1pjv8" />
            New visitor
          </button>
        </div>
        <p className="text-[10px] leading-relaxed text-slate-400" data-id="element-132" data-mp="3uvnn8xa2ddxp">{boothDisclaimer}</p>
      </div>
    </div>;
}